import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Trash2, ArrowLeft, ArrowRight } from "lucide-react";
import Footer from "../components/Footer";
import RecommendationRail from "../components/RecommendationRail";
import { useCart } from "../context/CartContext";
import { fmtPrice } from "../lib/api";
import { handleImgError } from "../lib/imgFallback";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, unitPriceOf } = useCart();

  return (
    <div className="pt-16 min-h-screen flex flex-col bg-[#0F0F10]">
      <section className="mx-auto max-w-[1200px] w-full px-5 sm:px-8 py-16 flex-1">
        <Link to="/products" className="inline-flex items-center gap-2 text-[13px] font-medium text-[#A8A8A8] hover:text-[#F5F5F5] transition-colors mb-8">
          <ArrowLeft className="h-4 w-4" /> Continue Shopping
        </Link>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-display font-extrabold text-4xl md:text-5xl tracking-tighter">Cart</motion.h1>

        {items.length === 0 ? (
          <div className="mt-12 rounded-[24px] border border-white/10 bg-[#1F1F1F] p-16 text-center">
            <p data-testid="cart-page-empty" className="text-[#7a7a7a] uppercase tracking-widest text-sm">Your cart is empty</p>
            <Link to="/products" className="btn-press mt-8 inline-block bg-[#4ADE80] text-[#0F0F10] font-medium px-8 py-4 hover:bg-[#6FE998]">Browse Catalog</Link>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 rounded-[24px] border border-white/10 divide-y divide-white/10 overflow-hidden">
              {items.map((i) => (
                <div key={i.slug} data-testid={`cart-page-item-${i.slug}`} className="flex gap-5 p-5 bg-[#0F0F10]">
                  <img src={i.image} alt={i.name} onError={handleImgError} className="h-24 w-24 object-cover rounded-2xl" />
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <div>
                        <h3 className="font-display font-bold text-lg tracking-tight">{i.name}</h3>
                        <p className="text-xs text-[#7a7a7a] mt-1">{i.size}</p>
                      </div>
                      <button onClick={() => removeItem(i.slug)} aria-label="Remove"><Trash2 className="h-4 w-4 text-[#7a7a7a] hover:text-[#EF4444] transition-colors" /></button>
                    </div>
                    <div className="flex justify-between items-center mt-4">
                      <div className="flex items-center border border-white/10 rounded-full">
                        <button onClick={() => updateQty(i.slug, i.quantity - 1)} className="p-2 hover:text-[#4ADE80]"><Minus className="h-3 w-3" /></button>
                        <span className="px-4 text-sm font-medium">{i.quantity}</span>
                        <button onClick={() => updateQty(i.slug, i.quantity + 1)} className="p-2 hover:text-[#4ADE80]"><Plus className="h-3 w-3" /></button>
                      </div>
                      <span className="font-display font-bold">{fmtPrice(unitPriceOf(i) * i.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-[24px] border border-white/10 bg-[#1F1F1F] p-6 h-fit">
              <h3 className="font-display font-bold text-xl tracking-tight border-b border-white/10 pb-4">Summary</h3>
              <div className="flex justify-between items-baseline mt-6">
                <span className="text-xs uppercase tracking-widest text-[#7a7a7a]">Subtotal</span>
                <span className="font-display font-extrabold text-3xl tracking-tight">{fmtPrice(subtotal)}</span>
              </div>
              <Link to="/checkout" data-testid="proceed-to-checkout-btn" className="btn-press mt-8 w-full bg-[#4ADE80] text-[#0F0F10] font-medium py-4 hover:bg-[#6FE998] hover:shadow-lg hover:shadow-[#4ADE80]/20 flex items-center justify-center gap-2">
                Proceed to Checkout <ArrowRight className="h-4 w-4" />
              </Link>
              <p className="mt-4 text-[10px] text-[#7a7a7a] text-center leading-relaxed">Card, PayPal &amp; Interac e-Transfer available at checkout. Research use only.</p>
            </div>
          </div>
        )}
      </section>
      <RecommendationRail title="Complete Your Research" />
      <Footer />
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
}
